import {
  type AdjustBalanceRequest,
  type AdminMenu,
  adminMenuSchema,
  type AdminMenuItem,
  adminMenuItemSchema,
  type AdminSettings,
  type AdminTopupPage,
  adminTopupPageSchema,
  type AdminTopupQuery,
  adminSettingsSchema,
  type AdminUser,
  type AdminUserPage,
  adminUserPageSchema,
  adminUserSchema,
  type AdminUserQuery,
  type BlockUserRequest,
  type MenuItemRequest,
  type UpdateMenuItemRequest,
  type RefreshModelsResult,
  refreshModelsResultSchema,
  type TestConnectionResult,
  type TestConnectionTarget,
  type TopupReport,
  topupReportSchema,
  testConnectionResultSchema,
  type UpdateSettingsRequest,
} from '@ytclip/shared'
import { z } from 'zod'
import { apiRequest } from '@/lib/api-client.ts'

/** Secret values come back empty — only whether they are set, and from where. */
export function fetchSettings(): Promise<AdminSettings> {
  return apiRequest('/admin/settings', adminSettingsSchema)
}

export function updateSettings(body: UpdateSettingsRequest): Promise<AdminSettings> {
  return apiRequest('/admin/settings', adminSettingsSchema, { method: 'PATCH', body })
}

export function testConnection(
  target: TestConnectionTarget,
  recipient?: string,
): Promise<TestConnectionResult> {
  return apiRequest(`/admin/settings/test/${target}`, testConnectionResultSchema, {
    method: 'POST',
    body: recipient ? { recipient } : {},
  })
}

export function fetchAdminUsers(query: AdminUserQuery): Promise<AdminUserPage> {
  return apiRequest('/admin/users', adminUserPageSchema, {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      search: query.search,
      sort: query.sort,
      kind: query.kind,
    },
  })
}

export function blockUser(userId: string, body: BlockUserRequest): Promise<AdminUser> {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/block`, adminUserSchema, {
    method: 'POST',
    body,
  })
}

export function unblockUser(userId: string): Promise<AdminUser> {
  return apiRequest(`/admin/users/${encodeURIComponent(userId)}/unblock`, adminUserSchema, {
    method: 'POST',
    body: {},
  })
}

const adjustBalanceResponseSchema = z.object({ balanceUsd: z.number() })

export function adjustUserBalance(
  userId: string,
  body: AdjustBalanceRequest,
): Promise<{ balanceUsd: number }> {
  return apiRequest(
    `/admin/users/${encodeURIComponent(userId)}/adjust-balance`,
    adjustBalanceResponseSchema,
    { method: 'POST', body },
  )
}

export function fetchTopupReport(): Promise<TopupReport> {
  return apiRequest('/admin/reports/topups', topupReportSchema)
}

export function fetchAdminTopups(query: AdminTopupQuery): Promise<AdminTopupPage> {
  return apiRequest('/admin/topups', adminTopupPageSchema, {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      search: query.search,
      status: query.status,
      from: query.from,
      to: query.to,
    },
  })
}

/** Drops the server's cached catalogue and reads LiteLLM again. */
export function refreshModels(): Promise<RefreshModelsResult> {
  return apiRequest('/admin/models/refresh', refreshModelsResultSchema, {
    method: 'POST',
    body: {},
  })
}

export function fetchMenu(): Promise<AdminMenu> {
  return apiRequest('/admin/app/menu', adminMenuSchema)
}

export function createMenuItem(body: MenuItemRequest): Promise<AdminMenuItem> {
  return apiRequest('/admin/app/menu', adminMenuItemSchema, { method: 'POST', body })
}

export function updateMenuItem(
  id: string,
  body: UpdateMenuItemRequest,
): Promise<AdminMenuItem> {
  return apiRequest(`/admin/app/menu/${encodeURIComponent(id)}`, adminMenuItemSchema, {
    method: 'PATCH',
    body,
  })
}

export function deleteMenuItem(id: string): Promise<void> {
  return apiRequest(`/admin/app/menu/${encodeURIComponent(id)}`, z.void(), {
    method: 'DELETE',
  })
}
